import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Link, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import './App.css';
import visaLogo from './assets/visa.png';
import mastercardLogo from './assets/mastercard.png';

const routerFuture = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
};

function getLangFromPathname(pathname) {
  return pathname === '/ua' || pathname.startsWith('/ua/') ? 'ua' : 'en';
}

function baseFromLang(lang) {
  return lang === 'ua' ? '/ua' : '';
}

function withBase(base, path) {
  if (!path.startsWith('/')) return path;
  if (path === '/') return base || '/';
  return `${base}${path}`;
}

function swapLangPath(pathname, targetLang) {
  const currentLang = getLangFromPathname(pathname);
  if (currentLang === targetLang) return pathname;

  const withoutUa = pathname.replace(/^\/ua(?=\/|$)/, '') || '/';
  if (targetLang === 'ua') {
    return withoutUa === '/' ? '/ua' : `/ua${withoutUa}`;
  }
  return withoutUa;
}


function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(!!mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);
  return reduced;
}

function useScrollSpy() {
  const [y, setY] = useState(0);
  useEffect(() => {
    const onScroll = () => setY(window.scrollY || 0);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return y;
}


async function apiPostJson(path, payload) {
  // In production (Vercel), REACT_APP_BACKEND_URL should be configured.
  // For local/dev behind the same proxy, we fallback to same-origin.
  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  const res = await fetch(`${baseUrl}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.detail || 'Request failed');
  }
  return data;
}

function postRedirect({ url, fields, testId }) {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = url;
  form.style.display = 'none';
  form.setAttribute('data-testid', testId || 'payment-redirect-form');

  Object.entries(fields || {}).forEach(([k, v]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = k;
    input.value = String(v);
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
}

async function apiGetJson(path) {
  // In production (Vercel), REACT_APP_BACKEND_URL should be configured.
  // For local/dev behind the same proxy, we fallback to same-origin.
  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  const res = await fetch(`${baseUrl}${path}`, { method: 'GET' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.detail || 'Request failed');
  }
  return data;
}


function useRevealOnScroll() {
  const reduced = useReducedMotion();
  useEffect(() => {
    if (reduced) return;
    const nodes = Array.from(document.querySelectorAll('[data-reveal]'));
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('reveal-in');
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    nodes.forEach((n) => obs.observe(n));
    return () => obs.disconnect();
  }, [reduced]);
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

function useLangBase() {
  const location = useLocation();
  const lang = getLangFromPathname(location.pathname);
  const base = baseFromLang(lang);
  return { location, lang, base };
}


function Container({ children, className = '' }) {
  return <div className={`mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>;
}

function Pill({ children, testId }) {
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/80 shadow-[0_0_0_1px_rgba(255,255,255,0.04)]"
    >
      {children}
    </span>
  );
}

function FooterPaymentLogos({ testIdPrefix = 'footer' }) {
  return (
    <div className="flex items-center gap-3" data-testid={`${testIdPrefix}-payment-logos`}>
      <img
        src={visaLogo}
        alt="Visa"
        className="h-5 w-auto opacity-80 grayscale transition-opacity hover:opacity-100"
        data-testid={`${testIdPrefix}-visa-logo`}
      />
      <img
        src={mastercardLogo}
        alt="Mastercard"
        className="h-5 w-auto opacity-80 grayscale transition-opacity hover:opacity-100"
        data-testid={`${testIdPrefix}-mastercard-logo`}
      />
    </div>
  );
}

function PrimaryButton({ to, href, onClick, children, testId, className = '' }) {
  const cls =
    "inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-500 via-violet-500 to-cyan-400 px-5 py-3 text-sm font-semibold text-ink-950 shadow-glow transition-transform duration-200 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/60 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-950";
  if (to) {
    return (
      <Link data-testid={testId} to={to} onClick={onClick} className={`${cls} ${className}`}>
        {children}
      </Link>
    );
  }
  if (href) {
    return (
      <a data-testid={testId} href={href} onClick={onClick} className={`${cls} ${className}`}>
        {children}
      </a>
    );
  }
  return (
    <button data-testid={testId} type="button" onClick={onClick} className={`${cls} ${className}`}>
      {children}
    </button>
  );
}

function SecondaryButton({ to, href, children, testId, className = '' }) {
  const cls =
    "inline-flex items-center justify-center gap-2 rounded-xl border border-white/12 bg-white/[0.03] px-5 py-3 text-sm font-semibold text-white/90 shadow-glow transition-colors duration-200 hover:bg-white/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-950";
  if (to) {
    return (
      <Link data-testid={testId} to={to} className={`${cls} ${className}`}>
        {children}
      </Link>
    );
  }
  return (
    <a data-testid={testId} href={href} className={`${cls} ${className}`}>
      {children}
    </a>
  );
}

function BrandMark() {
  return (
    <div className="flex items-center gap-2">
      <div className="relative h-9 w-9">
        <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-indigo-500 via-violet-500 to-cyan-400 opacity-95" />
        <div className="absolute inset-[1px] rounded-[11px] bg-ink-950" />
        <svg
          className="absolute inset-0 m-auto h-6 w-6"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M6 15.5c2.2-1.2 3.2-3.2 3.5-5.6.4-3 2.6-4.9 5.5-4.9 2.7 0 4.8 1.5 5.5 4.1"
            stroke="url(#g)"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M18 8.5c-2.2 1.2-3.2 3.2-3.5 5.6-.4 3-2.6 4.9-5.5 4.9-2.7 0-4.8-1.5-5.5-4.1"
            stroke="url(#g)"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <defs>
            <linearGradient id="g" x1="4" y1="4" x2="20" y2="20" gradientUnits="userSpaceOnUse">
              <stop stopColor="#A5B4FC" />
              <stop offset="0.5" stopColor="#C4B5FD" />
              <stop offset="1" stopColor="#67E8F9" />
            </linearGradient>
          </defs>
        </svg>
      </div>
      <div className="leading-tight">
        <div className="text-sm font-semibold tracking-tight text-white" data-testid="brand-name">
          NeuralForge
        </div>
        <div className="text-xs text-white/60" data-testid="brand-tagline">
          AI solutions that scale your business
        </div>
      </div>
    </div>
  );
}

function FloatingNav() {
  const y = useScrollSpy();
  const isScrolled = y > 16;
  const [open, setOpen] = useState(false);
  const { location, lang } = useLangBase();


  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const linkBase =
    "rounded-lg px-3 py-2 text-sm font-medium text-white/75 transition-colors hover:text-white hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20";
  const active = "bg-white/8 text-white";

  return (
    <div className="pointer-events-none fixed left-0 right-0 top-0 z-50">
      <Container className="pointer-events-auto">


        <div
          className={`mt-4 flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-ink-950/50 px-4 py-3 shadow-glow backdrop-blur transition-all ${
            isScrolled ? 'bg-ink-950/70' : ''
          }`}
          data-testid="floating-nav"
        >
          <Link to={withBase(baseFromLang(lang), '/')} className="shrink-0" data-testid="nav-home-link">
            <BrandMark />
          </Link>

          <div className="hidden items-center gap-1 lg:flex" data-testid="nav-desktop-links">
            <NavLink className={({ isActive }) => `${linkBase} ${isActive ? active : ''}`} to={withBase(baseFromLang(lang), '/services')} data-testid="nav-services">
              {lang === 'ua' ? 'Послуги' : 'Services'}
            </NavLink>
            <NavLink className={({ isActive }) => `${linkBase} ${isActive ? active : ''}`} to={withBase(baseFromLang(lang), '/how-it-works')} data-testid="nav-how-it-works">
              {lang === 'ua' ? 'Як це працює' : 'How It Works'}
            </NavLink>
            <NavLink className={({ isActive }) => `${linkBase} ${isActive ? active : ''}`} to={withBase(baseFromLang(lang), '/case-studies')} data-testid="nav-case-studies">
              {lang === 'ua' ? 'Кейси' : 'Case Studies'}
            </NavLink>
            <NavLink className={({ isActive }) => `${linkBase} ${isActive ? active : ''}`} to={withBase(baseFromLang(lang), '/resources')} data-testid="nav-resources">
              {lang === 'ua' ? 'Ресурси' : 'Resources'}
            </NavLink>
            <NavLink className={({ isActive }) => `${linkBase} ${isActive ? active : ''}`} to={withBase(baseFromLang(lang), '/subscription')} data-testid="nav-subscription">
              {lang === 'ua' ? 'Підписка' : 'Subscription'}
            </NavLink>
            <NavLink className={({ isActive }) => `${linkBase} ${isActive ? active : ''}`} to={withBase(baseFromLang(lang), '/about')} data-testid="nav-about">
              {lang === 'ua' ? 'Про нас' : 'About'}
            </NavLink>
          </div>

          <div className="hidden items-center gap-3 lg:flex">
            <div className="hidden items-center gap-2 lg:flex" data-testid="nav-lang-switcher">
              <Link
                to={swapLangPath(location.pathname, 'en')}
                className={`rounded-lg px-2 py-1 text-xs font-semibold transition-colors ${lang === 'en' ? 'bg-white/10 text-white' : 'text-white/65 hover:text-white hover:bg-white/5'}`}
                data-testid="lang-en"
              >
                EN
              </Link>
              <Link
                to={swapLangPath(location.pathname, 'ua')}
                className={`rounded-lg px-2 py-1 text-xs font-semibold transition-colors ${lang === 'ua' ? 'bg-white/10 text-white' : 'text-white/65 hover:text-white hover:bg-white/5'}`}
                data-testid="lang-ua"
              >
                UA
              </Link>
            </div>
            <PrimaryButton to={withBase(baseFromLang(lang), '/contact')} testId="nav-contact-primary">
              {lang === 'ua' ? 'Звʼязатися' : 'Contact Us'}
            </PrimaryButton>
          </div>

          <button
            type="button"
            className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm font-semibold text-white/90 lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            data-testid="nav-mobile-toggle"
          >
            <span className="sr-only">Open menu</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {open ? (
          <div
            className="mt-3 rounded-2xl border border-white/10 bg-ink-950/80 p-3 shadow-glow backdrop-blur lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="grid gap-1">
              <NavLink className={({ isActive }) => `${linkBase} ${isActive ? active : ''}`} to={withBase(baseFromLang(lang), '/services')} data-testid="nav-mobile-services">
                {lang === 'ua' ? 'Послуги' : 'Services'}
              </NavLink>
              <NavLink className={({ isActive }) => `${linkBase} ${isActive ? active : ''}`} to={withBase(baseFromLang(lang), '/how-it-works')} data-testid="nav-mobile-how-it-works">
                {lang === 'ua' ? 'Як це працює' : 'How It Works'}
              </NavLink>
              <NavLink className={({ isActive }) => `${linkBase} ${isActive ? active : ''}`} to={withBase(baseFromLang(lang), '/case-studies')} data-testid="nav-mobile-case-studies">
                {lang === 'ua' ? 'Кейси' : 'Case Studies'}
              </NavLink>
              <NavLink className={({ isActive }) => `${linkBase} ${isActive ? active : ''}`} to={withBase(baseFromLang(lang), '/resources')} data-testid="nav-mobile-resources">
                {lang === 'ua' ? 'Ресурси' : 'Resources'}
              </NavLink>
              <NavLink className={({ isActive }) => `${linkBase} ${isActive ? active : ''}`} to={withBase(baseFromLang(lang), '/subscription')} data-testid="nav-mobile-subscription">
                {lang === 'ua' ? 'Підписка' : 'Subscription'}
              </NavLink>
              <NavLink className={({ isActive }) => `${linkBase} ${isActive ? active : ''}`} to={withBase(baseFromLang(lang), '/about')} data-testid="nav-mobile-about">
                {lang === 'ua' ? 'Про нас' : 'About'}
              </NavLink>

            </div>
            <div className="mt-3">
              <div className="flex items-center justify-between gap-2" data-testid="nav-mobile-lang-switcher">
                <Link
                  to={swapLangPath(location.pathname, 'en')}
                  className={`flex-1 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-center text-xs font-semibold ${
                    lang === 'en' ? 'text-white' : 'text-white/65'
                  }`}
                  data-testid="lang-en-mobile"
                >
                  EN
                </Link>
                <Link
                  to={swapLangPath(location.pathname, 'ua')}
                  className={`flex-1 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-center text-xs font-semibold ${
                    lang === 'ua' ? 'text-white' : 'text-white/65'
                  }`}
                  data-testid="lang-ua-mobile"
                >
                  UA
                </Link>
              </div>
              <PrimaryButton to={withBase(baseFromLang(lang), '/contact')} testId="nav-mobile-contact-primary" className="w-full">
                {lang === 'ua' ? 'Звʼязатися' : 'Contact Us'}
              </PrimaryButton>
            </div>
          </div>
        ) : null}
      </Container>
    </div>
  );
}

function Footer() {
  const { lang } = useLangBase();
  const base = baseFromLang(lang);

 

  const copy = useMemo(() => {
    if (lang === 'ua') {
      return {
        companyTitle: 'Компанія',
        solutionsTitle: 'Рішення',
        resourcesTitle: 'Ресурси',
        about: 'Про нас',
        caseStudies: 'Кейси',
        services: 'Послуги',
        howItWorks: 'Як це працює',
        blog: 'Блог',
        subscription: 'Підписка',
        contact: 'Звʼязатися',
        acceptedPayment: 'Приймаємо оплату'
      };
    }
    return {
      companyTitle: 'Company',
      solutionsTitle: 'Solutions',
      resourcesTitle: 'Resources',
      about: 'About',
      caseStudies: 'Case Studies',
      services: 'Services',
      howItWorks: 'How It Works',
      blog: 'Blog',
      subscription: 'Subscription',
      contact: 'Contact Us',
      acceptedPayment: 'Accepted payment methods'
    };
  }, [lang]);

  return (
    <footer className="border-t border-white/10 bg-black/20" data-testid="site-footer">
      <Container className="py-10">
        <div className="grid gap-8 md:grid-cols-12">
          <div className="md:col-span-5">
            <BrandMark />
            <p className="mt-4 text-sm text-white/70" data-testid="footer-description">
              NeuralForge builds practical AI systems—agents, automation, and analytics—designed for measurable business impact.
            </p>

          </div>
          <div className="md:col-span-7">
            <div className="grid grid-cols-2 gap-6 sm:grid-cols-3">
              <div>
                <div className="text-sm font-semibold text-white" data-testid="footer-links-company-title">{copy.companyTitle}</div>
                <div className="mt-3 grid gap-2 text-sm text-white/70">
                  <Link to={withBase(base, '/about')} data-testid="footer-link-about">{copy.about}</Link>
                  <Link to={withBase(base, '/case-studies')} data-testid="footer-link-case-studies">{copy.caseStudies}</Link>
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold text-white" data-testid="footer-links-solutions-title">{copy.solutionsTitle}</div>
                <div className="mt-3 grid gap-2 text-sm text-white/70">
                  <Link to={withBase(base, '/services')} data-testid="footer-link-services">{copy.services}</Link>
                  <Link to={withBase(base, '/how-it-works')} data-testid="footer-link-how-it-works">{copy.howItWorks}</Link>
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold text-white" data-testid="footer-links-resources-title">{copy.resourcesTitle}</div>
                <div className="mt-3 grid gap-2 text-sm text-white/70">
                  <Link to={withBase(base, '/resources')} data-testid="footer-link-resources">{copy.blog}</Link>
                  <Link to={withBase(base, '/subscription')} data-testid="footer-link-subscription">{copy.subscription}</Link>
                  <Link to={withBase(base, '/contact')} data-testid="footer-link-contact">{copy.contact}</Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-col justify-between gap-4 border-t border-white/10 pt-6 text-xs text-white/55 sm:flex-row sm:items-end">
          <div>
            <div data-testid="footer-copyright">© {new Date().getFullYear()} NeuralForge. All rights reserved.</div>
            <div className="mt-3 flex items-end gap-3" data-testid="footer-payment-area">
              <FooterPaymentLogos testIdPrefix="footer" />
              <div className="text-[11px] text-white/45" data-testid="footer-payment-note">
                {copy.acceptedPayment}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-2" data-testid="footer-legal-links">
            {lang === 'ua' ? (
              <>
                <Link to={withBase(base, '/offer-agreement')} data-testid="footer-link-offer-agreement" className="hover:text-white/80">
                  Договір оферти
                </Link>
                <Link to={withBase(base, '/personal-data-policy')} data-testid="footer-link-personal-data-policy" className="hover:text-white/80">
                  Політика захисту та обробки персональних даних
                </Link>
              </>
            ) : (
              <>
                <Link to={withBase(base, '/privacy-policy')} data-testid="footer-link-privacy-policy" className="hover:text-white/80">
                  Privacy Policy
                </Link>
                <Link to={withBase(base, '/terms-of-service')} data-testid="footer-link-terms-of-service" className="hover:text-white/80">
                  Terms of Service
                </Link>
              </>
            )}
          </div>
        </div>
      </Container>
    </footer>
  );
}

function Hero({ lang = 'en', base = '' }) {
  const reduced = useReducedMotion();
  const baseSafe = base || '';
  return (
    <section className="relative overflow-hidden pt-28" data-testid="home-hero">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-grid opacity-[0.16]" />
        <div className="absolute -left-28 top-24 h-80 w-80 rounded-full bg-indigo-500/25 blur-3xl" />
        <div className="absolute -right-28 top-10 h-80 w-80 rounded-full bg-cyan-400/15 blur-3xl" />
        {!reduced ? (
          <div className="absolute left-1/2 top-[180px] h-[520px] w-[520px] -translate-x-1/2 rounded-full border border-white/10 bg-white/[0.02]" />
        ) : null}
      </div>

      <Container className="relative">
        <div className="grid items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Pill testId="hero-pill">
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
              {lang === 'ua' ? 'AI консалтинг • агенти • автоматизація • аналітика' : 'AI consulting • agents • automation • analytics'}
            </Pill>
            <h1
              className="mt-5 text-balance text-4xl font-semibold tracking-tight text-white sm:text-5xl lg:text-6xl"
              data-testid="hero-title"
            >
              {lang === 'ua' ? 'Створюйте AI-системи, які працюють у продакшні.' : 'Build AI systems that ship.'}
              <span className="block bg-gradient-to-r from-indigo-200 via-violet-200 to-cyan-200 bg-clip-text text-transparent">
                {lang === 'ua' ? 'Масштабуйте результат — а не складність.' : 'Scale outcomes—not complexity.'}
              </span>
            </h1>
            <p className="mt-5 max-w-2xl text-pretty text-lg text-white/70" data-testid="hero-subtitle">
              {lang === 'ua'
                ? 'NeuralForge впроваджує AI-рішення під ключ: від стратегії та MLOps до агентів, автоматизації та прогнозування. Ми фокусуємося на вимірюваному бізнес-ефекті.'
                : 'NeuralForge delivers end-to-end AI solutions: from strategy and MLOps to production-grade agents, automation, and forecasting. We focus on measurable business impact.'}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <PrimaryButton to={withBase(baseSafe, '/contact')} testId="hero-primary-cta">
                {lang === 'ua' ? 'Звʼязатися' : 'Contact Us'}
              </PrimaryButton>
              <SecondaryButton to={withBase(baseSafe, '/services')} testId="hero-secondary-cta">
                {lang === 'ua' ? 'Переглянути послуги' : 'Explore Services'}
              </SecondaryButton>
            </div>

            <div className="mt-10 grid max-w-2xl grid-cols-3 gap-3" data-testid="hero-metrics">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 shadow-glow">
                <div className="text-sm font-semibold text-white" data-testid="metric-1-value">Weeks</div>
                <div className="mt-1 text-xs text-white/60" data-testid="metric-1-label">to first production release</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 shadow-glow">
                <div className="text-sm font-semibold text-white" data-testid="metric-2-value">Human-in-loop</div>
                <div className="mt-1 text-xs text-white/60" data-testid="metric-2-label">guardrails + evaluation</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 shadow-glow">
                <div className="text-sm font-semibold text-white" data-testid="metric-3-value">Enterprise-ready</div>
                <div className="mt-1 text-xs text-white/60" data-testid="metric-3-label">security & reliability</div>
              </div>
            </div>
          </div>

          <div className="relative lg:col-span-5" data-testid="hero-visual">
            <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-6 shadow-glow">
              <div className="absolute inset-0 opacity-90">
                <div className="absolute -left-20 -top-20 h-64 w-64 rounded-full bg-indigo-500/20 blur-3xl" />
                <div className="absolute -right-24 bottom-[-60px] h-72 w-72 rounded-full bg-cyan-400/15 blur-3xl" />
              </div>
              <div className="relative">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-white" data-testid="hero-visual-title">AI Delivery System</div>
                  <Pill testId="hero-visual-pill">Live pipeline</Pill>
                </div>

                <div className="mt-6 grid gap-3">
                  {[
                    { label: 'Discovery & ROI Model', tone: 'from-emerald-400/25 to-transparent' },
                    { label: 'Agent + Workflow Design', tone: 'from-indigo-400/25 to-transparent' },
                    { label: 'Implementation (NLP / MLOps)', tone: 'from-violet-400/25 to-transparent' },
                    { label: 'Evaluation & Monitoring', tone: 'from-cyan-400/25 to-transparent' },
                    { label: 'Production Deployment', tone: 'from-fuchsia-400/20 to-transparent' }
                  ].map((row, idx) => (
                    <div
                      key={row.label}
                      className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-r ${row.tone} p-4`}
                      data-testid={`hero-visual-row-${idx}`}
                    >
                      <div className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                        <div className="absolute inset-0 bg-white/[0.03]" />
                      </div>
                      <div className="relative flex items-center justify-between">
                        <div className="text-sm text-white/90">{row.label}</div>
                        <div className="text-xs text-white/60">→</div>
                      </div>
                    </div>
                  ))}
                </div>

                {!reduced ? (
                  <div className="mt-6 grid grid-cols-3 gap-3">
                    <div className="float-y rounded-2xl border border-white/10 bg-white/[0.03] p-3 text-xs text-white/70" data-testid="hero-chip-1">
                      Agent orchestration
                    </div>
                    <div
                      className="float-y rounded-2xl border border-white/10 bg-white/[0.03] p-3 text-xs text-white/70"
                      style={{ animationDelay: '0.8s' }}
                      data-testid="hero-chip-2"
                    >
                      Tool calling
                    </div>
                    <div
                      className="float-y rounded-2xl border border-white/10 bg-white/[0.03] p-3 text-xs text-white/70"
                      style={{ animationDelay: '1.6s' }}
                      data-testid="hero-chip-3"
                    >
                      Eval harness
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div
              className="pointer-events-none absolute -bottom-8 left-1/2 hidden w-[92%] -translate-x-1/2 rounded-3xl border border-white/10 bg-gradient-to-r from-indigo-500/10 via-violet-500/10 to-cyan-400/10 p-6 shadow-glow sm:block"
              data-testid="hero-visual-floating-panel"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-white">Outcome-focused delivery</div>
                  <div className="mt-1 text-xs text-white/60">
                    We ship quickly, measure impact, and iterate with guardrails.
                  </div>
                </div>
                <div className="rounded-xl bg-white/[0.06] px-3 py-2 text-xs text-white/70">98th percentile uptime targets</div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

function SectionHeader({ eyebrow, title, description, testId }) {
  return (
    <div className="max-w-2xl" data-testid={testId}>
      <div className="text-xs font-semibold uppercase tracking-wider text-white/55">{eyebrow}</div>
      <h2 className="mt-3 text-2xl font-semibold tracking-tight text-white sm:text-3xl">{title}</h2>
      <p className="mt-3 text-white/70">{description}</p>
    </div>
  );
}

function Card({ children, className = '', testId }) {
  return (
    <div
      data-testid={testId}
      data-reveal
      className={`reveal-out rounded-3xl border border-white/10 bg-white/[0.03] p-6 shadow-glow ${className}`}
    >
      {children}
    </div>
  );
}

function HomePage({ lang = 'en', base = '' }) {
  useRevealOnScroll();
  const baseSafe = base || '';

  const modules = [
    {
      title: 'AI Agents',
      desc: 'Autonomous workflows for support, ops, sales, and internal knowledge—connected to your systems with guardrails.',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M7 8.5A4.5 4.5 0 0 1 11.5 4h1A4.5 4.5 0 0 1 17 8.5V10h1a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-1v1.5A4.5 4.5 0 0 1 12.5 24h-1A4.5 4.5 0 0 1 7 19.5V18H6a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h1V8.5Z" fill="rgba(255,255,255,0.12)" />
          <path d="M9 12h6M9 15h4" stroke="rgba(255,255,255,0.7)" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      ),
      accent: 'from-indigo-500/25 via-violet-500/15 to-transparent'
    },
    {
      title: 'Platforms & MLOps',
      desc: 'Production ML infrastructure: deployment pipelines, evaluation harnesses, monitoring, and secure model operations.',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M4 7.5 12 3l8 4.5v9L12 21l-8-4.5v-9Z" fill="rgba(255,255,255,0.12)" />
          <path d="M12 3v18M4 7.5l8 4.5 8-4.5" stroke="rgba(255,255,255,0.7)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      accent: 'from-cyan-400/20 via-indigo-500/10 to-transparent'
    },
    {
      title: 'Analytics & Forecasting',
      desc: 'Predict demand, churn, and risk with modern forecasting. Turn messy data into decision-ready dashboards.',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M5 18V6m0 12h14" stroke="rgba(255,255,255,0.7)" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M7 14.5 10 11l2.5 2.5L17.5 8" stroke="rgba(255,255,255,0.7)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="17.5" cy="8" r="1.5" fill="rgba(255,255,255,0.35)" />
        </svg>
      ),
      accent: 'from-emerald-400/20 via-cyan-400/10 to-transparent'
    }
  ];

  const testimonials = [
    {
      quote:
        'NeuralForge delivered an AI support agent that cut resolution time by 38% while improving customer satisfaction in the first month.',
      name: 'VP Customer Experience',
      company: 'B2B SaaS (Series C)'
    },
    {
      quote:
        'Their team operationalized MLOps and evaluation so we could deploy models safely across teams—with monitoring and clear KPIs.',
      name: 'Head of Data',
      company: 'Logistics & Supply Chain'
    },
    {
      quote:
        'We launched forecasting that improved inventory planning and reduced stockouts. The process was pragmatic and fast.',
      name: 'Director of Operations',
      company: 'Retail & E-commerce'
    }
  ];

  return (
    <div data-testid="home-page">
      <Hero lang={lang} base={baseSafe} />

      <section className="relative py-20" data-testid="home-modules">
        <Container>
          <SectionHeader
            testId="home-modules-header"
            eyebrow="Solutions"
            title="AI solutions built for real workflows"
            description="From agents to platforms, we design modular systems that integrate with your stack, ship quickly, and stay reliable in production."
          />

          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            {modules.map((m) => (
              <Card key={m.title} testId={`module-card-${m.title.replaceAll(' ', '-').toLowerCase()}-${lang}`}>
                <div className={`rounded-2xl border border-white/10 bg-gradient-to-r ${m.accent} p-4`}>{m.icon}</div>
                <div className="mt-5 text-lg font-semibold text-white">{m.title}</div>
                <div className="mt-2 text-sm text-white/70">{m.desc}</div>
                <div className="mt-5">
                  <SecondaryButton to={withBase(baseSafe, '/services')} testId={`module-card-cta-${m.title.replaceAll(' ', '-').toLowerCase()}-${lang}`}>
                    {lang === 'ua' ? 'Детальніше' : 'Learn more'}
                  </SecondaryButton>
                </div>
              </Card>
            ))}
          </div>

          <div
            className="mt-10 flex flex-col items-start justify-between gap-6 rounded-3xl border border-white/10 bg-gradient-to-r from-white/[0.06] via-white/[0.03] to-white/[0.02] p-8 shadow-glow sm:flex-row sm:items-center"
            data-reveal
            data-testid="home-cta-band"
          >
            <div>
              <div className="text-lg font-semibold text-white" data-testid="home-cta-title">Ready to put AI into production?</div>
              <div className="mt-1 text-sm text-white/70" data-testid="home-cta-subtitle">
                Share your use case—we’ll propose a pragmatic pilot with clear success metrics.
              </div>
            </div>
            <PrimaryButton to="/contact" testId="home-cta-button">Contact Us</PrimaryButton>
          </div>
        </Container>
      </section>

      <section className="relative py-20" data-testid="home-testimonials">
        <Container>
          <SectionHeader
            testId="home-testimonials-header"
            eyebrow="Proof"
            title="Trusted by teams that measure impact"
            description="Outcome-driven delivery, clear evaluation, and production reliability—without the hype."
          />
          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            {testimonials.map((t, i) => (
              <Card key={t.name} testId={`testimonial-${i}`} className="relative">
                <div className="text-sm leading-relaxed text-white/80" data-testid={`testimonial-quote-${i}`}>
                  “{t.quote}”
                </div>
                <div className="mt-6">
                  <div className="text-sm font-semibold text-white" data-testid={`testimonial-name-${i}`}>{t.name}</div>
                  <div className="text-xs text-white/60" data-testid={`testimonial-company-${i}`}>{t.company}</div>
                </div>
                <div className="pointer-events-none absolute right-6 top-6 h-20 w-20 rounded-full bg-gradient-to-br from-indigo-500/20 to-cyan-400/10 blur-2xl" />
              </Card>
            ))}
          </div>
        </Container>
      </section>
    </div>
  );
}

const SERVICES = [
  {
    key: 'consulting',
    title: 'AI Consulting',
    titleUa: 'AI консалтинг',
    short: 'Strategy, NLP, MLOps, and production rollout with measurable KPIs.',
    shortUa: 'Стратегія, NLP, MLOps та запуск у продакшн з вимірюваними KPI.',
    price: 'Upon request (fixed scope)',
    priceUa: 'За запитом (фіксований обсяг)',
    bullets: ['Discovery & ROI model', 'Architecture + guardrails', 'MLOps pipelines and evaluation', 'Production rollout'],
    bulletsUa: ['Discovery та ROI-модель', 'Архітектура та запобіжники', 'MLOps пайплайни та оцінювання', 'Запуск у продакшн'],
    accent: 'from-indigo-500/25 via-violet-500/15 to-transparent'
  },
  {
    key: 'automation',
    title: 'Process Automation',
    titleUa: 'Автоматизація процесів',
    short: 'AI agents for support, ops, sales, and back office—integrated with your tools.',
    shortUa: 'AI-агенти для підтримки, операцій, продажів та бек-офісу — інтеграція з вашими системами.',
    price: 'From $900 / month',
    priceUa: 'Від $900 / місяць',
    bullets: ['Support triage + drafting', 'CRM workflows', 'HR & internal knowledge', 'Approval and escalation flows'],
    bulletsUa: ['Тріаж і чернетки відповідей', 'CRM-воркфлоу', 'HR та внутрішні знання', 'Погодження та ескалації'],
    accent: 'from-emerald-400/20 via-cyan-400/10 to-transparent'
  },
  {
    key: 'custom',
    title: 'Custom AI Solutions',
    titleUa: 'Індивідуальні AI-рішення',
    short: 'Tailored RAG, copilots, and tool-using assistants designed for your domain.',
    shortUa: 'RAG, копайлоти та асистенти з інструментами під вашу предметну область.',
    price: 'From $2,500 (one-time)',
    priceUa: 'Від $2,500 (разово)',
    bullets: ['RAG & semantic search', 'Tool use integrations', 'Evaluation + monitoring', 'Security-minded delivery'],
    bulletsUa: ['RAG і семантичний пошук', 'Інтеграції з інструментами', 'Оцінювання та моніторинг', 'Безпека за дизайном'],
    accent: 'from-cyan-400/20 via-indigo-500/10 to-transparent'
  },
  {
    key: 'genai',
    title: 'Generative AI Tools',
    titleUa: 'Генеративні AI-інструменти',
    short: 'Chatbots and content workflows with safety, evaluation, and cost controls.',
    shortUa: 'Чат-боти та контент-воркфлоу з безпекою, оцінюванням і контролем витрат.',
    price: 'From $600 / month',
    priceUa: 'Від $600 / місяць',
    bullets: ['Chatbots with tool use', 'Content generation workflows', 'Quality evaluation', 'Usage analytics'],
    bulletsUa: ['Чат-боти з інструментами', 'Генерація контенту', 'Оцінювання якості', 'Аналітика використання'],
    accent: 'from-fuchsia-400/18 via-violet-500/10 to-transparent'
  },
  {
    key: 'analytics',
    title: 'Data Analytics',
    titleUa: 'Аналітика даних',
    short: 'Forecasting, dashboards, and predictive models that support decisions.',
    shortUa: 'Прогнозування, дашборди та моделі, що допомагають приймати рішення.',
    price: 'From $750 / month',
    priceUa: 'Від $750 / місяць',
    bullets: ['Forecasting & scenario planning', 'Anomaly detection', 'Churn / risk modeling', 'Executive dashboards'],
    bulletsUa: ['Прогнозування та сценарії', 'Виявлення аномалій', 'Моделі відтоку/ризику', 'Дашборди для керівництва'],
    accent: 'from-amber-300/15 via-emerald-400/10 to-transparent'
  }
];

function ServicesPage() {
  useRevealOnScroll();
  const { lang, base } = useLangBase();

  const [openKey, setOpenKey] = useState('');
  return (
    <div className="pt-28" data-testid="services-page">
      <Container>
        <SectionHeader
          testId="services-header"
          eyebrow={lang === 'ua' ? 'Послуги' : 'Services'}
          title={lang === 'ua' ? 'Послуги та ціни' : 'Services & Pricing'}
          description={
            lang === 'ua'
              ? 'Оберіть послугу, перегляньте деталі та надішліть запит. Це UX-флоу без оплати та без бекенду.'
              : 'Select a service, view details, and request it. This is a UI-only flow—no payment processing or backend.'
          }
        />

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          {SERVICES.map((s) => {
            const isOpen = openKey === s.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => setOpenKey((k) => (k === s.key ? '' : s.key))}
                className={`text-left rounded-3xl border border-white/10 bg-white/[0.03] p-6 shadow-glow transition-transform duration-200 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20 ${
                  isOpen ? 'ring-1 ring-white/15' : ''
                }`}
                data-reveal
                data-testid={`service-card-${s.key}`}
                aria-expanded={isOpen}
              >
                <div className={`rounded-2xl border border-white/10 bg-gradient-to-r ${s.accent} p-4`}>
                  <div className="flex items-center justify-between">
                    <div className="text-base font-semibold text-white" data-testid={`service-card-title-${s.key}`}>{lang === 'ua' ? s.titleUa : s.title}</div>
                    <div className="text-xs text-white/60" data-testid={`service-card-toggle-${s.key}`}>{isOpen ? (lang === 'ua' ? 'Закрити' : 'Close') : (lang === 'ua' ? 'Деталі' : 'Expand')}</div>
                  </div>
                  <div className="mt-2 text-sm text-white/70" data-testid={`service-card-short-${s.key}`}>{lang === 'ua' ? s.shortUa : s.short}</div>
                  <div className="mt-3 inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-white/75" data-testid={`service-card-price-${s.key}`}>
                    <span className="text-white/55">{lang === 'ua' ? 'Ціна:' : 'Price:'}</span> {lang === 'ua' ? s.priceUa : s.price}
                  </div>
                </div>

                {isOpen ? (
                  <div className="mt-5 grid gap-2" data-testid={`service-card-details-${s.key}`}>
                    {(lang === 'ua' ? s.bulletsUa : s.bullets).map((b) => (
                      <div key={b} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                        <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/10 text-[11px] text-white/80">
                          ✓
                        </span>
                        <div className="text-sm text-white/80">{b}</div>
                      </div>
                    ))}
                    <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                      <PrimaryButton
                        to={withBase(base, `/contact?service=${encodeURIComponent(s.key)}`)}
                        testId={`service-card-cta-order-${s.key}`}
                      >
                        {lang === 'ua' ? 'Замовити / Запит' : 'Order / Request service'}
                      </PrimaryButton>
                      <SecondaryButton to={withBase(base, '/case-studies')} testId={`service-card-cta-cases-${s.key}`}>
                        {lang === 'ua' ? 'Переглянути кейси' : 'See Case Studies'}
                      </SecondaryButton>
                    </div>
                  </div>
                ) : (
                  <div className="mt-5 text-xs text-white/55" data-testid={`service-card-hint-${s.key}`}>
                    Click to view deliverables and example outcomes.
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </Container>
    </div>
  );
}

function HowItWorksPage() {
  useRevealOnScroll();
  const steps = [
    {
      title: 'Discovery & ROI',
      desc: 'Map workflows, data, constraints, and success metrics. Align stakeholders and pick a pilot that proves value quickly.'
    },
    {
      title: 'Design & Guardrails',
      desc: 'Choose architectures (RAG, agents, models). Define evaluation, safety rules, and human-in-loop controls.'
    },
    {
      title: 'Build & Integrate',
      desc: 'Implement pipelines and integrations. Connect tools, data sources, and APIs. Ship iteratively.'
    },
    {
      title: 'Evaluate & Monitor',
      desc: 'Run offline evaluation, live shadow tests, and monitoring. Measure quality, cost, latency, and risk.'
    },
    {
      title: 'Deploy & Optimize',
      desc: 'Roll out with clear ownership, SLAs, and dashboards. Improve over time with feedback loops and retraining.'
    }
  ];

  return (
    <div className="pt-28" data-testid="how-it-works-page">
      <Container>
        <SectionHeader
          testId="how-it-works-header"
          eyebrow="Process"
          title="A proven path from idea to production"
          description="A simple, repeatable workflow designed to reduce risk and ship measurable outcomes."
        />

        <div className="mt-12 grid gap-6 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className="grid gap-4" data-testid="how-it-works-steps">
              {steps.map((s, idx) => (
                <div
                  key={s.title}
                  className="reveal-out rounded-3xl border border-white/10 bg-white/[0.03] p-6 shadow-glow"
                  data-reveal
                  data-testid={`how-step-${idx}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="mt-1 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white/8 text-sm font-semibold text-white" data-testid={`how-step-num-${idx}`}>
                      {idx + 1}
                    </div>
                    <div>
                      <div className="text-base font-semibold text-white" data-testid={`how-step-title-${idx}`}>{s.title}</div>
                      <div className="mt-1 text-sm text-white/70" data-testid={`how-step-desc-${idx}`}>{s.desc}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="reveal-out sticky top-28 rounded-3xl border border-white/10 bg-white/[0.03] p-6 shadow-glow" data-reveal data-testid="how-it-works-diagram">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-white" data-testid="how-diagram-title">Workflow diagram</div>
                <Pill testId="how-diagram-pill">Animated</Pill>
              </div>

              <div className="mt-6">
                <svg
                  viewBox="0 0 420 360"
                  className="h-auto w-full"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-label="How it works diagram"
                  data-testid="how-diagram-svg"
                >
                  <defs>
                    <linearGradient id="wg" x1="50" y1="40" x2="370" y2="320" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#A5B4FC" />
                      <stop offset="0.55" stopColor="#C4B5FD" />
                      <stop offset="1" stopColor="#67E8F9" />
                    </linearGradient>
                    <filter id="blur" x="-20%" y="-20%" width="140%" height="140%">
                      <feGaussianBlur stdDeviation="12" />
                    </filter>
                  </defs>

                  <path
                    d="M70 70C120 40 160 40 210 70C260 100 300 100 350 70"
                    stroke="url(#wg)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    className="path-draw"
                  />
                  <path
                    d="M70 290C120 320 160 320 210 290C260 260 300 260 350 290"
                    stroke="url(#wg)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    className="path-draw"
                    style={{ animationDelay: '0.6s' }}
                  />

                  {[{ x: 70, y: 70, t: 'Discover' },
                    { x: 350, y: 70, t: 'Design' },
                    { x: 70, y: 180, t: 'Build' },
                    { x: 350, y: 180, t: 'Evaluate' },
                    { x: 210, y: 290, t: 'Deploy' }].map((n) => (
                    <g key={n.t}>
                      <circle cx={n.x} cy={n.y} r="30" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.12)" />
                      <circle cx={n.x} cy={n.y} r="18" fill="url(#wg)" opacity="0.12" filter="url(#blur)" />
                      <circle cx={n.x} cy={n.y} r="6" fill="url(#wg)" />
                      <text x={n.x} y={n.y + 50} textAnchor="middle" fontSize="12" fill="rgba(255,255,255,0.7)">
                        {n.t}
                      </text>
                    </g>
                  ))}
                </svg>

                <p className="mt-5 text-sm text-white/70" data-testid="how-diagram-caption">
                  A lightweight delivery system: structured discovery, safe design, fast integration, rigorous evaluation, and controlled rollout.
                </p>

                <div className="mt-6">
                  <PrimaryButton to="/contact" testId="how-it-works-primary-cta" className="w-full">
                    Contact Us
                  </PrimaryButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}

const CASE_STUDIES = [
  {
    title: 'Customer Support Agent for B2B SaaS',
    industry: 'B2B SaaS',
    problem: 'High ticket volume, slow resolution, inconsistent responses.',
    solution: 'RAG + tool-using agent integrated with ticketing and knowledge base, with escalation guardrails.',
    impact: ['38% faster resolution time', 'Improved CSAT', 'Reduced agent workload'],
    metric: '38%'
  },
  {
    title: 'Forecasting for Inventory Planning',
    industry: 'Retail / E-commerce',
    problem: 'Stockouts and over-ordering due to noisy demand patterns.',
    solution: 'Time-series forecasting + anomaly detection with dashboards and scenario planning.',
    impact: ['Fewer stockouts', 'Better purchasing decisions', 'Clear KPI tracking'],
    metric: '↓ stockouts'
  },
  {
    title: 'Operations Automation for Sales Ops',
    industry: 'B2B Services',
    problem: 'Manual research and repetitive CRM updates slowing down pipeline.',
    solution: 'Agent workflows for enrichment, summarization, and automated updates with approvals.',
    impact: ['Hours saved per rep/week', 'Better lead quality', 'Cleaner CRM data'],
    metric: '+hours'
  }
];

function CaseStudiesPage() {
  useRevealOnScroll();
  return (
    <div className="pt-28" data-testid="case-studies-page">
      <Container>
        <SectionHeader
          testId="case-studies-header"
          eyebrow="Case Studies"
          title="AI implementations with business impact"
          description="Examples of how we deliver measurable outcomes with production-ready systems."
        />

        <div className="mt-10 grid gap-6 lg:grid-cols-3" data-testid="case-studies-grid">
          {CASE_STUDIES.map((c, idx) => (
            <Card key={c.title} testId={`case-study-${idx}`} className="relative overflow-hidden">
              <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gradient-to-br from-indigo-500/20 to-cyan-400/10 blur-2xl" />
              <div className="relative">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-white/55" data-testid={`case-industry-${idx}`}>{c.industry}</div>
                    <div className="mt-2 text-lg font-semibold text-white" data-testid={`case-title-${idx}`}>{c.title}</div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm font-semibold text-white" data-testid={`case-metric-${idx}`}>
                    {c.metric}
                  </div>
                </div>

                <div className="mt-5 grid gap-4 text-sm">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-white/55">Problem</div>
                    <div className="mt-1 text-white/75" data-testid={`case-problem-${idx}`}>{c.problem}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-white/55">Solution</div>
                    <div className="mt-1 text-white/75" data-testid={`case-solution-${idx}`}>{c.solution}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-white/55">Impact</div>
                    <div className="mt-2 grid gap-2" data-testid={`case-impact-${idx}`}>
                      {c.impact.map((i) => (
                        <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-3 text-white/75">
                          {i}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <PrimaryButton to="/contact" testId={`case-cta-${idx}`}>Contact Us</PrimaryButton>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </Container>
    </div>
  );
}

function AboutPage() {
  useRevealOnScroll();
  const values = [
    {
      title: 'Outcome-first',
      desc: 'We start with measurable business value and build only what moves the needle.'
    },
    {
      title: 'Reliability by design',
      desc: 'Evaluation, monitoring, and guardrails are built in—not bolted on.'
    },
    {
      title: 'Human-in-loop',
      desc: 'We design AI to support teams with transparency, control, and escalation.'
    }
  ];

  const partners = ['OpenAI ecosystems', 'Cloud providers', 'Data platforms', 'Security & compliance'];

  return (
    <div className="pt-28" data-testid="about-page">
      <Container>
        <SectionHeader
          testId="about-header"
          eyebrow="About"
          title="A practical AI partner for modern teams"
          description="NeuralForge helps organizations adopt AI responsibly and effectively—turning prototypes into production systems."
        />

        <div className="mt-10 grid gap-6 lg:grid-cols-3" data-testid="about-values">
          {values.map((v, idx) => (
            <Card key={v.title} testId={`about-value-${idx}`}>
              <div className="text-lg font-semibold text-white" data-testid={`about-value-title-${idx}`}>{v.title}</div>
              <div className="mt-2 text-sm text-white/70" data-testid={`about-value-desc-${idx}`}>{v.desc}</div>
            </Card>
          ))}
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Card testId="about-mission">
              <div className="text-sm font-semibold text-white/80" data-testid="about-mission-eyebrow">Mission</div>
              <div className="mt-2 text-2xl font-semibold tracking-tight text-white" data-testid="about-mission-title">
                Build trustworthy AI that scales.
              </div>
              <p className="mt-3 text-sm text-white/70" data-testid="about-mission-text">
                We help teams identify high-leverage AI opportunities, build systems with clear evaluation, and ship into production with reliability.
                Our approach blends modern LLM techniques, traditional ML, and robust engineering.
              </p>
            </Card>
          </div>

          <div className="lg:col-span-5">
            <Card testId="about-partners">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-white" data-testid="about-partners-title">Partners</div>
                <Pill testId="about-partners-pill">Ecosystem</Pill>
              </div>
              <div className="mt-5 grid gap-2" data-testid="about-partners-list">
                {partners.map((p) => (
                  <div key={p} className="rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-3 text-sm text-white/75">
                    {p}
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <PrimaryButton to="/contact" testId="about-primary-cta" className="w-full">Contact Us</PrimaryButton>
              </div>
            </Card>
          </div>
        </div>
      </Container>
    </div>
  );
}

const RESOURCES = [
  {
    slug: 'ai-agents-guardrails',
    type: 'Blog',
    title: 'Agent guardrails that actually work in production',
    excerpt: 'A practical checklist for evaluation, tool access control, and escalation patterns for enterprise AI agents.',
    date: 'Aug 2025',
    readTime: '6 min'
  },
  {
    slug: 'rag-eval-harness',
    type: 'Whitepaper',
    title: 'RAG evaluation: how to measure retrieval and generation quality',
    excerpt: 'A lightweight framework for offline tests, golden sets, and monitoring that keeps retrieval systems reliable.',
    date: 'Aug 2025',
    readTime: '10 min'
  },
  {
    slug: 'mlops-to-llmops',
    type: 'Guide',
    title: 'From MLOps to LLMOps: operational patterns for modern AI',
    excerpt: 'Deployment, observability, and cost controls for LLM-backed products and internal tooling.',
    date: 'Aug 2025',
    readTime: '8 min'
  }
];

function ResourcesPage() {
  useRevealOnScroll();
  const { lang, base } = useLangBase();
 

  return (
    <div className="pt-28" data-testid="resources-page">
      <Container>
        <SectionHeader
          testId="resources-header"
          eyebrow={lang === 'ua' ? 'Ресурси' : 'Resources'}
          title={lang === 'ua' ? 'Гіди, документація та практичні матеріали' : 'Guides, docs, and practical AI playbooks'}
          description={
            lang === 'ua'
              ? 'Поки що — статичні приклади (зберігаються в коді). Згодом можна розширити до повного CMS.'
              : 'Static sample content for now (stored in code). We can evolve this into a full CMS later.'
          }
        />

        <div className="mt-10 grid gap-6 lg:grid-cols-3" data-testid="resources-grid">
          {RESOURCES.map((r, idx) => (
            <Card key={r.slug} testId={`resource-${idx}`} className="flex flex-col">
              <div className="flex items-center justify-between">
                <Pill testId={`resource-type-${idx}`}>{r.type}</Pill>
                <div className="text-xs text-white/60" data-testid={`resource-meta-${idx}`}>
                  {r.date} • {r.readTime}
                </div>
              </div>
              <div className="mt-4 text-lg font-semibold text-white" data-testid={`resource-title-${idx}`}>{r.title}</div>
              <div className="mt-2 text-sm text-white/70" data-testid={`resource-excerpt-${idx}`}>{r.excerpt}</div>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => {}}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-white/90 hover:text-white"
                  data-testid={`resource-read-${idx}`}
                >
                  Read →
                </button>
              </div>
              <div className="mt-auto" />
            </Card>
          ))}
        </div>

        <div
          className="mt-10 rounded-3xl border border-white/10 bg-gradient-to-r from-indigo-500/10 via-violet-500/10 to-cyan-400/10 p-8 shadow-glow"
          data-reveal
          data-testid="resources-cta"
        >
          <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
            <div>
              <div className="text-lg font-semibold text-white" data-testid="resources-cta-title">Need a tailored playbook?</div>
              <div className="mt-1 text-sm text-white/70" data-testid="resources-cta-subtitle">
                We’ll share a recommended architecture and phased plan for your use case.
              </div>
            </div>
            <PrimaryButton to={withBase(base, '/contact')} testId="resources-cta-button">Contact Us</PrimaryButton>
          </div>
        </div>
      </Container>
    </div>
  );
}

function ContactPage() {
  useRevealOnScroll();
  const location = useLocation();
  const lang = getLangFromPathname(location.pathname);
  const base = baseFromLang(lang);

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const selectedServiceKey = query.get('service');
  const selectedService = useMemo(
    () => SERVICES.find((s) => s.key === selectedServiceKey) || null,
    [selectedServiceKey]
  );

  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const isUa = lang === 'ua';

  // Minimal contact info only - full details in legal pages
  const contactInfo = useMemo(
    () => ({
      email: 'derevankodmitro79@gmail.com',
      phoneDisplay: '+380 95 978 3620',
      phoneHref: '+380959783620'
    }),
    []
  );

  const mailto = useMemo(() => {
    const subject = encodeURIComponent(`NeuralForge inquiry — ${form.company || 'Company'}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nCompany: ${form.company}\n\nMessage:\n${form.message}`
    );
    return `mailto:${contactInfo.email}?subject=${subject}&body=${body}`;
  }, [form, contactInfo.email]);

  useEffect(() => {
    if (!selectedService) return;
    setForm((f) => {
      const prefix = lang === 'ua' ? 'Запит послуги:' : 'Service request:';
      const name = lang === 'ua' ? selectedService.titleUa : selectedService.title;


      const line = `${prefix} ${name}`;
      if (f.message.includes(line)) return f;
      return { ...f, message: f.message ? `${line}\n\n${f.message}` : `${line}\n` };
    });
  }, [selectedService, lang]);

  const canSubmit = form.name.trim() && form.email.trim() && form.message.trim();

  return (
    <div className="pt-28" data-testid="contact-page">
      <Container>
        <SectionHeader
          testId="contact-header"
          eyebrow="Contact"
          title="Let’s talk about your AI roadmap"
          description="Tell us about your use case. We’ll respond with a suggested approach, timeline, and next steps."
        />

        <div className="mt-10 grid gap-6 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Card testId="contact-form-card">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!canSubmit) return;
                  setSubmitted(true);
                  window.location.href = mailto;
                }}
                className="grid gap-4"
                data-testid="contact-form"
              >
                {selectedService ? (
                  <div
                    className="rounded-3xl border border-white/10 bg-gradient-to-r from-indigo-500/10 via-violet-500/10 to-cyan-400/10 p-5"
                    data-reveal
                    data-testid="contact-selected-service"
                  >
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-white/55" data-testid="contact-selected-service-label">
                          {lang === 'ua' ? 'Обрана послуга' : 'Selected service'}
                        </div>
                        <div className="mt-1 text-lg font-semibold text-white" data-testid="contact-selected-service-title">
                          {lang === 'ua' ? selectedService.titleUa : selectedService.title}
                        </div>
                        <div className="mt-1 text-sm text-white/70" data-testid="contact-selected-service-price">
                          {lang === 'ua' ? 'Ціна:' : 'Price:'} {lang === 'ua' ? selectedService.priceUa : selectedService.price}
                        </div>
                      </div>
                      <Link
                        to={withBase(base, '/services')}
                        className="text-sm font-semibold text-white/80 hover:text-white"
                        data-testid="contact-selected-service-change"
                      >
                        {lang === 'ua' ? 'Змінити →' : 'Change →'}
                      </Link>
                    </div>
                  </div>
                ) : null}

                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="grid gap-2 text-sm" data-testid="contact-name-field">
                    <span className="text-white/80">{lang === 'ua' ? 'Імʼя' : 'Name'}</span>
                    <input
                      data-testid="contact-name-input"
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      className="h-11 rounded-xl border border-white/10 bg-white/[0.03] px-3 text-white/90 outline-none focus:border-white/20 focus:ring-2 focus:ring-cyan-300/30"
                      placeholder={lang === 'ua' ? 'Ваше імʼя' : 'Your name'}
                      autoComplete="name"
                    />
                  </label>
                  <label className="grid gap-2 text-sm" data-testid="contact-email-field">
                    <span className="text-white/80">Email</span>
                    <input


                      data-testid="contact-email-input"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      className="h-11 rounded-xl border border-white/10 bg-white/[0.03] px-3 text-white/90 outline-none focus:border-white/20 focus:ring-2 focus:ring-cyan-300/30"
                      placeholder="you@company.com"
                      autoComplete="email"
                      type="email"
                    />
                  </label>
                </div>

                <label className="grid gap-2 text-sm" data-testid="contact-company-field">
                  <span className="text-white/80">Company (optional)</span>
                  <input
                    data-testid="contact-company-input"
                    value={form.company}
                    onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                    className="h-11 rounded-xl border border-white/10 bg-white/[0.03] px-3 text-white/90 outline-none focus:border-white/20 focus:ring-2 focus:ring-cyan-300/30"
                    placeholder="Company name"
                    autoComplete="organization"
                  />
                </label>

                <label className="grid gap-2 text-sm" data-testid="contact-message-field">
                  <span className="text-white/80">How can we help?</span>
                  <textarea
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                    className="min-h-[140px] resize-y rounded-xl border border-white/10 bg-white/[0.03] px-3 py-3 text-white/90 outline-none focus:border-white/20 focus:ring-2 focus:ring-cyan-300/30"
                    placeholder="Briefly describe your goals, timeline, and constraints."
                  />
                </label>

                {!canSubmit ? (
                  <div className="rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-3 text-xs text-white/65" data-testid="contact-validation-hint">
                    Please provide your name, email, and a short message.
                  </div>
                ) : null}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <PrimaryButton testId="contact-submit-button" className="sm:w-auto" onClick={() => {}}>
                    Send message
                  </PrimaryButton>
                </div>

                {submitted ? (
                  <div className="rounded-2xl border border-emerald-400/20 bg-emerald-400/10 px-4 py-3 text-xs text-emerald-100" data-testid="contact-success">
                    Thanks—your email client should open with a prefilled message.
                  </div>
                ) : null}
              </form>
            </Card>
          </div>

          <div className="lg:col-span-5">
            <Card testId="contact-side-card" className="sticky top-28">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-white" data-testid="contact-side-title">
                  {isUa ? 'Контакти' : 'Contact'}
                </div>
                <Pill testId="contact-side-pill">Info</Pill>
              </div>

              <div className="mt-4 rounded-3xl border border-white/10 bg-white/[0.02] p-5" data-testid="contact-business-box">
                <div className="grid gap-3 text-sm text-white/75" data-testid="contact-details">
                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-3 hover:bg-white/[0.05]"
                    data-testid="contact-email"
                  >
                    <span className="text-white/55">Email:</span> {contactInfo.email}
                  </a>
                  <a
                    href={`tel:${contactInfo.phoneHref}`}
                    className="rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-3 hover:bg-white/[0.05]"
                    data-testid="contact-phone"
                  >
                    <span className="text-white/55">{isUa ? 'Телефон:' : 'Phone:'}</span> {contactInfo.phoneDisplay}
                  </a>
                </div>

                <div className="mt-4 text-xs text-white/50" data-testid="contact-legal-note">
                  {isUa 
                    ? 'Повна юридична інформація доступна на сторінках «Договір оферти» та «Політика захисту персональних даних».'
                    : 'Full legal and business details are available in our Terms of Service and Privacy Policy.'}
                </div>
              </div>

              <div className="mt-5 rounded-3xl border border-white/10 bg-white/[0.02] p-5" data-testid="contact-business-how-it-works">
                <div className="text-xs font-semibold uppercase tracking-wider text-white/55" data-testid="contact-business-how-it-works-title">
                  {isUa ? 'Як це працює' : 'How access works'}
                </div>
                <div className="mt-3 grid gap-2 text-sm text-white/75" data-testid="contact-business-how-it-works-steps">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                    {isUa ? '1) Зв\'яжіться з нами через цей сайт.' : '1) Contact us via this website.'}
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                    {isUa ? '2) Ми проведемо первинну консультацію для визначення обсягу та термінів.' : '2) We run an initial consultation to define scope and timeline.'}
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                    {isUa ? '3) Ви отримаєте цифровий доступ після онбордингу.' : '3) You receive digital access after onboarding.'}
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <SecondaryButton to={withBase(base, '/services')} testId="contact-side-services" className="w-full">
                  {isUa ? 'Переглянути послуги' : 'Explore Services'}
                </SecondaryButton>
              </div>
            </Card>
          </div>
        </div>
      </Container>
    </div>
  );
}

function LegalPage({ title, testId, children }) {
  useRevealOnScroll();
  return (
    <div className="pt-28" data-testid={testId}>
      <Container>
        <div className="reveal-out rounded-3xl border border-white/10 bg-white/[0.03] p-10 shadow-glow" data-reveal>
          <div className="text-xs font-semibold uppercase tracking-wider text-white/55">Legal</div>
          <div className="mt-3 text-3xl font-semibold tracking-tight text-white">{title}</div>
          <div className="mt-4 space-y-4 text-sm text-white/75">{children}</div>
        </div>
      </Container>
    </div>
  );
}

function PrivacyPolicyPage() {
  return (
    <LegalPage title="Privacy Policy" testId="privacy-policy-page">
      <div className="space-y-6">
        <section>
          <h2 className="text-lg font-semibold text-white mb-3">1. General Provisions</h2>
          <p>1.1. This Privacy Policy (hereinafter — "Policy") defines the procedure for collecting, storing, using, and protecting personal data of users of the website neuralforge.ink.</p>
          <p className="mt-2">1.2. The data controllers are:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Derevianko Dmytro Oleksandrovych (Tax ID: 3667308138)</li>
            <li>Individual Entrepreneur Boriak Oleksandr Valeriiovych (Tax ID: 3576902677)</li>
          </ul>
          <p className="mt-2">1.3. By using this website, you agree to the terms of this Policy.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">2. What Data We Collect</h2>
          <p>2.1. We may collect the following categories of personal data:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li><strong>Contact data:</strong> name, email address, phone number</li>
            <li><strong>Company data:</strong> organization name, position</li>
            <li><strong>Technical data:</strong> IP address, browser type, device information</li>
            <li><strong>Payment data:</strong> transaction information (processed by UPC payment system)</li>
            <li><strong>Communication data:</strong> content of messages sent through the contact form</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">3. Purpose of Personal Data Processing</h2>
          <p>3.1. Personal data is processed for the purpose of:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Providing services and fulfilling contractual obligations</li>
            <li>Responding to inquiries and requests</li>
            <li>Improving service quality</li>
            <li>Compliance with Ukrainian legislation</li>
            <li>Protecting the rights and legitimate interests of the Controller</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">4. Legal Basis for Processing</h2>
          <p>4.1. Personal data processing is carried out on the basis of:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Consent of the data subject</li>
            <li>Necessity for contract performance</li>
            <li>Legitimate interests of the Controller</li>
            <li>Compliance with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">5. Data Transfer to Third Parties</h2>
          <p>5.1. Personal data may be transferred to:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Payment systems (UPC) — for payment processing</li>
            <li>Hosting providers — to ensure website operation</li>
            <li>Government authorities — in cases provided by law</li>
          </ul>
          <p className="mt-2">5.2. We do not sell or transfer personal data to third parties for marketing purposes.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">6. Personal Data Protection</h2>
          <p>6.1. We implement technical and organizational measures to protect personal data:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Data encryption (SSL/TLS)</li>
            <li>Restricted access to personal data</li>
            <li>Regular security system updates</li>
            <li>Staff training on data protection</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">7. Data Retention Periods</h2>
          <p>7.1. Personal data is retained for:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>The duration of the contract and 3 years after its termination</li>
            <li>Periods established by legislation for accounting documents</li>
            <li>Until consent is withdrawn (for marketing communications)</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">8. Rights of Data Subjects</h2>
          <p>8.1. In accordance with Ukrainian Law "On Personal Data Protection", you have the right to:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Know the sources of collection and location of your personal data</li>
            <li>Receive information about the conditions of data access</li>
            <li>Access your personal data</li>
            <li>Request correction of inaccurate data</li>
            <li>Request data deletion</li>
            <li>Withdraw consent for data processing</li>
            <li>Challenge data processing actions</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">9. Cookies</h2>
          <p>9.1. The website may use cookies for:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Ensuring website functionality</li>
            <li>Analyzing website usage</li>
            <li>Content personalization</li>
          </ul>
          <p className="mt-2">9.2. You can configure cookie usage in your browser settings.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">10. Contact Information</h2>
          <p>10.1. For questions regarding personal data processing, please contact:</p>
          
          <div className="mt-3 p-4 rounded-xl border border-white/10 bg-white/[0.02]">
            <h3 className="font-semibold text-white mb-2">Controller 1:</h3>
            <p><strong>Derevianko Dmytro Oleksandrovych</strong></p>
            <p>Tax ID (IPN): 3667308138</p>
            <p>Address: Poltava region, Myrhorod district, Lokhvytsia, Kotliarevskoho str., 52</p>
            <p>Phone: +380 95 978 3620</p>
            <p>Email: derevankodmitro79@gmail.com</p>
            <p>IBAN: UA233077700000026002411243050</p>
          </div>

          <div className="mt-4 p-4 rounded-xl border border-white/10 bg-white/[0.02]">
            <h3 className="font-semibold text-white mb-2">Controller 2:</h3>
            <p>Individual Entrepreneur <strong>Boriak Oleksandr Valeriiovych</strong></p>
            <p>Tax ID (EDRPOU): 3576902677</p>
            <p>IBAN: UA743220010000026001370059837</p>
            <p>Bank: Joint Stock Company &quot;UNIVERSAL BANK&quot;, MFO: 322001, EDRPOU: 21133352</p>
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">11. Changes to the Policy</h2>
          <p>11.1. We reserve the right to make changes to this Policy.</p>
          <p className="mt-2">11.2. The current version of the Policy is always available on this page.</p>
        </section>

        <p className="text-white/60 text-xs mt-6">Last updated: January 2026</p>
      </div>
    </LegalPage>
  );
}

function TermsOfServicePage() {
  return (
    <LegalPage title="Terms of Service" testId="terms-of-service-page">
      <div className="space-y-6">
        <section>
          <h2 className="text-lg font-semibold text-white mb-3">1. General Provisions</h2>
          <p>1.1. This Public Offer Agreement (hereinafter — "Agreement") is an official proposal (public offer) from the Sellers — individual entrepreneurs listed in the "Details" section, to enter into an agreement for the provision of services in IT consulting, business process automation, and AI solutions implementation.</p>
          <p className="mt-2">1.2. In accordance with Articles 633 and 641 of the Civil Code of Ukraine, this Agreement is a public offer, and upon acceptance of the terms set forth below, any individual or legal entity accepting this offer becomes the Customer (Buyer).</p>
          <p className="mt-2">1.3. Acceptance of this Agreement includes: placing an order through the website neuralforge.ink; payment for services; written or electronic confirmation of the order.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">2. Subject of the Agreement</h2>
          <p>2.1. The Seller undertakes to provide the Customer with services in the field of:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>IT consulting and technical audit</li>
            <li>Development and implementation of AI solutions</li>
            <li>Business process automation</li>
            <li>Software integration</li>
            <li>Technical support and maintenance</li>
          </ul>
          <p className="mt-2">2.2. The Customer undertakes to pay for the services in accordance with the terms of this Agreement and the agreed estimate.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">3. Service Delivery Procedure</h2>
          <p>3.1. Services are provided in digital format after an initial consultation and approval of the technical specification.</p>
          <p className="mt-2">3.2. Delivery timelines are agreed upon individually for each project.</p>
          <p className="mt-2">3.3. Access to digital services is granted after payment confirmation.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">4. Pricing and Payment Procedure</h2>
          <p>4.1. Service pricing is determined according to the selected tariff plan or individual estimate.</p>
          <p className="mt-2">4.2. Payment is made in the national currency of Ukraine (hryvnia) through the UPC payment system.</p>
          <p className="mt-2">4.3. Services are considered paid from the moment funds are credited to the Seller&apos;s account.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">5. Rights and Obligations of the Parties</h2>
          <p>5.1. The Seller undertakes to:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Provide services with quality and within established deadlines</li>
            <li>Ensure confidentiality of Customer information</li>
            <li>Inform the Customer about the progress of work</li>
          </ul>
          <p className="mt-3">5.2. The Customer undertakes to:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Pay for services in a timely manner</li>
            <li>Provide necessary information for work execution</li>
            <li>Accept completed work in accordance with established procedures</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">6. Refund Policy</h2>
          <p>6.1. Refunds are provided in the following cases:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Inability to provide services due to the Seller&apos;s fault</li>
            <li>Customer&apos;s refusal of services before they begin</li>
          </ul>
          <p className="mt-2">6.2. Refund requests should be submitted to the Seller&apos;s email address within 14 days.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">7. Liability of the Parties</h2>
          <p>7.1. The parties are liable for non-performance or improper performance of their obligations in accordance with the current legislation of Ukraine.</p>
          <p className="mt-2">7.2. The Seller is not liable for damages caused by improper use of services by the Customer.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">8. Force Majeure</h2>
          <p>8.1. The parties are released from liability for partial or complete non-performance of obligations if it was the result of force majeure circumstances.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">9. Seller Details</h2>
          <div className="mt-3 p-4 rounded-xl border border-white/10 bg-white/[0.02]">
            <h3 className="font-semibold text-white mb-2">Seller 1:</h3>
            <p><strong>Derevianko Dmytro Oleksandrovych</strong></p>
            <p>Tax ID (IPN): 3667308138</p>
            <p>Address: Poltava region, Myrhorod district, Lokhvytsia, Kotliarevskoho str., 52</p>
            <p>Phone: +380 95 978 3620</p>
            <p>Email: derevankodmitro79@gmail.com</p>
            <p>IBAN: UA233077700000026002411243050</p>
          </div>

          <div className="mt-4 p-4 rounded-xl border border-white/10 bg-white/[0.02]">
            <h3 className="font-semibold text-white mb-2">Seller 2:</h3>
            <p>Individual Entrepreneur <strong>Boriak Oleksandr Valeriiovych</strong></p>
            <p>Tax ID (EDRPOU): 3576902677</p>
            <p>IBAN: UA743220010000026001370059837</p>
            <p>Bank: Joint Stock Company &quot;UNIVERSAL BANK&quot;, MFO: 322001, EDRPOU: 21133352</p>
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">10. Final Provisions</h2>
          <p>10.1. This Agreement becomes effective from the moment of acceptance and remains in force until the parties fully fulfill their obligations.</p>
          <p className="mt-2">10.2. All disputes are resolved through negotiations, and in case of failure to reach an agreement — in court in accordance with the legislation of Ukraine.</p>
          <p className="mt-2">10.3. The Seller reserves the right to make changes to this Agreement with prior notification on the website.</p>
        </section>

        <p className="text-white/60 text-xs mt-6">Last updated: January 2026</p>
      </div>
    </LegalPage>
  );
}

function OfferAgreementPageUA() {
  return (
    <LegalPage title="Договір оферти" testId="offer-agreement-page">
      <div className="space-y-6">
        <section>
          <h2 className="text-lg font-semibold text-white mb-3">1. Загальні положення</h2>
          <p>1.1. Цей Договір публічної оферти (далі — «Договір») є офіційною пропозицією (публічною офертою) від Продавців — фізичних осіб-підприємців, зазначених у розділі «Реквізити», укласти договір про надання послуг у сфері IT-консалтингу, автоматизації бізнес-процесів та впровадження AI-рішень.</p>
          <p className="mt-2">1.2. Відповідно до ст. 633, 641 Цивільного кодексу України, цей Договір є публічною офертою, і в разі прийняття викладених нижче умов, фізична або юридична особа, яка здійснює акцепт цієї оферти, стає Замовником (Покупцем).</p>
          <p className="mt-2">1.3. Акцептом (прийняттям) умов цього Договору є: оформлення замовлення через веб-сайт neuralforge.ink; оплата послуг; письмове або електронне підтвердження замовлення.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">2. Предмет договору</h2>
          <p>2.1. Продавець зобов&apos;язується надати Замовнику послуги у сфері:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>IT-консалтинг та технічний аудит</li>
            <li>Розробка та впровадження AI-рішень</li>
            <li>Автоматизація бізнес-процесів</li>
            <li>Інтеграція програмного забезпечення</li>
            <li>Технічна підтримка та супровід</li>
          </ul>
          <p className="mt-2">2.2. Замовник зобов&apos;язується оплатити послуги відповідно до умов цього Договору та погодженого кошторису.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">3. Порядок надання послуг</h2>
          <p>3.1. Послуги надаються у цифровому форматі після первинної консультації та погодження технічного завдання.</p>
          <p className="mt-2">3.2. Терміни виконання узгоджуються індивідуально для кожного проєкту.</p>
          <p className="mt-2">3.3. Доступ до цифрових послуг надається після підтвердження оплати.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">4. Вартість та порядок оплати</h2>
          <p>4.1. Вартість послуг визначається відповідно до обраного тарифного плану або індивідуального кошторису.</p>
          <p className="mt-2">4.2. Оплата здійснюється у національній валюті України (гривня) через платіжну систему UPC.</p>
          <p className="mt-2">4.3. Послуги вважаються оплаченими з моменту зарахування коштів на рахунок Продавця.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">5. Права та обов&apos;язки сторін</h2>
          <p>5.1. Продавець зобов&apos;язується:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Надавати послуги якісно та у встановлені терміни</li>
            <li>Забезпечувати конфіденційність інформації Замовника</li>
            <li>Інформувати Замовника про хід виконання робіт</li>
          </ul>
          <p className="mt-3">5.2. Замовник зобов&apos;язується:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Своєчасно оплачувати послуги</li>
            <li>Надавати необхідну інформацію для виконання робіт</li>
            <li>Приймати виконані роботи у встановленому порядку</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">6. Повернення коштів</h2>
          <p>6.1. Повернення коштів здійснюється у випадках:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Неможливості надання послуг з вини Продавця</li>
            <li>Відмови Замовника від послуг до початку їх надання</li>
          </ul>
          <p className="mt-2">6.2. Заявка на повернення подається на електронну адресу Продавця протягом 14 днів.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">7. Відповідальність сторін</h2>
          <p>7.1. Сторони несуть відповідальність за невиконання або неналежне виконання своїх зобов&apos;язань згідно з чинним законодавством України.</p>
          <p className="mt-2">7.2. Продавець не несе відповідальності за збитки, спричинені неправильним використанням послуг Замовником.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">8. Форс-мажор</h2>
          <p>8.1. Сторони звільняються від відповідальності за часткове або повне невиконання зобов&apos;язань, якщо воно стало наслідком обставин непереборної сили (форс-мажор).</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">9. Реквізити Продавців</h2>
          <div className="mt-3 p-4 rounded-xl border border-white/10 bg-white/[0.02]">
            <h3 className="font-semibold text-white mb-2">Продавець 1:</h3>
            <p><strong>Деревʼянко Дмитро Олександрович</strong></p>
            <p>ІПН / РНОКПП: 3667308138</p>
            <p>Адреса: Полтавська обл., Миргородський район, м. Лохвиця, вул. Котляревського, 52</p>
            <p>Телефон: +380 95 978 3620</p>
            <p>E-mail: derevankodmitro79@gmail.com</p>
            <p>IBAN: UA233077700000026002411243050</p>
          </div>

          <div className="mt-4 p-4 rounded-xl border border-white/10 bg-white/[0.02]">
            <h3 className="font-semibold text-white mb-2">Продавець 2:</h3>
            <p>ФОП <strong>Боряк Олександр Валерійович</strong></p>
            <p>ІПН / ЄДРПОУ: 3576902677</p>
            <p>IBAN: UA743220010000026001370059837</p>
            <p>Банк: Акціонерне товариство «УНІВЕРСАЛ БАНК», МФО: 322001, ЄДРПОУ Банку: 21133352</p>
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">10. Прикінцеві положення</h2>
          <p>10.1. Цей Договір набуває чинності з моменту акцепту і діє до повного виконання сторонами своїх зобов&apos;язань.</p>
          <p className="mt-2">10.2. Усі спори вирішуються шляхом переговорів, а в разі недосягнення згоди — у судовому порядку відповідно до законодавства України.</p>
          <p className="mt-2">10.3. Продавець залишає за собою право вносити зміни до цього Договору з попереднім повідомленням на сайті.</p>
        </section>

        <p className="text-white/60 text-xs mt-6">Дата останнього оновлення: січень 2026 р.</p>
      </div>
    </LegalPage>
  );
}

function PersonalDataPolicyPageUA() {
  return (
    <LegalPage title="Політика захисту та обробки персональних даних" testId="personal-data-policy-page">
      <div className="space-y-6">
        <section>
          <h2 className="text-lg font-semibold text-white mb-3">1. Загальні положення</h2>
          <p>1.1. Ця Політика захисту та обробки персональних даних (далі — «Політика») визначає порядок збору, зберігання, використання та захисту персональних даних користувачів веб-сайту neuralforge.ink.</p>
          <p className="mt-2">1.2. Володільцями персональних даних є:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Деревʼянко Дмитро Олександрович (ІПН: 3667308138)</li>
            <li>ФОП Боряк Олександр Валерійович (ЄДРПОУ: 3576902677)</li>
          </ul>
          <p className="mt-2">1.3. Використовуючи цей веб-сайт, ви погоджуєтесь з умовами цієї Політики.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">2. Які дані ми збираємо</h2>
          <p>2.1. Ми можемо збирати такі категорії персональних даних:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li><strong>Контактні дані:</strong> імя, електронна адреса, номер телефону</li>
            <li><strong>Дані компанії:</strong> назва організації, посада</li>
            <li><strong>Технічні дані:</strong> IP-адреса, тип браузера, дані про пристрій</li>
            <li><strong>Платіжні дані:</strong> інформація про транзакції (обробляється платіжною системою UPC)</li>
            <li><strong>Комунікаційні дані:</strong> зміст повідомлень, надісланих через форму зворотного звязку</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">3. Мета обробки персональних даних</h2>
          <p>3.1. Персональні дані обробляються з метою:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Надання послуг та виконання договірних зобовязань</li>
            <li>Відповіді на запити та звернення</li>
            <li>Покращення якості послуг</li>
            <li>Виконання вимог законодавства України</li>
            <li>Захисту прав та законних інтересів Володільця</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">4. Правові підстави обробки</h2>
          <p>4.1. Обробка персональних даних здійснюється на підставі:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Згоди субєкта персональних даних</li>
            <li>Необхідності виконання договору</li>
            <li>Законних інтересів Володільця</li>
            <li>Виконання юридичних зобовязань</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">5. Передача даних третім особам</h2>
          <p>5.1. Персональні дані можуть передаватися:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Платіжним системам (UPC) — для обробки платежів</li>
            <li>Хостинг-провайдерам — для забезпечення роботи сайту</li>
            <li>Державним органам — у випадках, передбачених законом</li>
          </ul>
          <p className="mt-2">5.2. Ми не продаємо та не передаємо персональні дані третім особам для маркетингових цілей.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">6. Захист персональних даних</h2>
          <p>6.1. Ми вживаємо технічних та організаційних заходів для захисту персональних даних:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Шифрування даних (SSL/TLS)</li>
            <li>Обмеження доступу до персональних даних</li>
            <li>Регулярне оновлення систем безпеки</li>
            <li>Навчання персоналу з питань захисту даних</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">7. Строки зберігання даних</h2>
          <p>7.1. Персональні дані зберігаються протягом:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Строку дії договору та 3 роки після його закінчення</li>
            <li>Строків, встановлених законодавством для бухгалтерських документів</li>
            <li>До моменту відкликання згоди (для маркетингових комунікацій)</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">8. Права субєктів персональних даних</h2>
          <p>8.1. Відповідно до Закону України «Про захист персональних даних», ви маєте право:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Знати про джерела збору та місцезнаходження своїх персональних даних</li>
            <li>Отримувати інформацію про умови надання доступу до даних</li>
            <li>На доступ до своїх персональних даних</li>
            <li>Вимагати виправлення неточних даних</li>
            <li>Вимагати видалення даних</li>
            <li>Відкликати згоду на обробку даних</li>
            <li>Оскаржити дії щодо обробки даних</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">9. Файли cookie</h2>
          <p>9.1. Веб-сайт може використовувати файли cookie для:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Забезпечення функціонування сайту</li>
            <li>Аналізу використання сайту</li>
            <li>Персоналізації контенту</li>
          </ul>
          <p className="mt-2">9.2. Ви можете налаштувати використання cookie у своєму браузері.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">10. Контактна інформація</h2>
          <p>10.1. З питань обробки персональних даних звертайтесь:</p>
          
          <div className="mt-3 p-4 rounded-xl border border-white/10 bg-white/[0.02]">
            <h3 className="font-semibold text-white mb-2">Володілець 1:</h3>
            <p><strong>Деревʼянко Дмитро Олександрович</strong></p>
            <p>ІПН / РНОКПП: 3667308138</p>
            <p>Адреса: Полтавська обл., Миргородський район, м. Лохвиця, вул. Котляревського, 52</p>
            <p>Телефон: +380 95 978 3620</p>
            <p>E-mail: derevankodmitro79@gmail.com</p>
            <p>IBAN: UA233077700000026002411243050</p>
          </div>

          <div className="mt-4 p-4 rounded-xl border border-white/10 bg-white/[0.02]">
            <h3 className="font-semibold text-white mb-2">Володілець 2:</h3>
            <p>ФОП <strong>Боряк Олександр Валерійович</strong></p>
            <p>ІПН / ЄДРПОУ: 3576902677</p>
            <p>IBAN: UA743220010000026001370059837</p>
            <p>Банк: Акціонерне товариство «УНІВЕРСАЛ БАНК», МФО: 322001, ЄДРПОУ Банку: 21133352</p>
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">11. Зміни до Політики</h2>
          <p>11.1. Ми залишаємо за собою право вносити зміни до цієї Політики.</p>
          <p className="mt-2">11.2. Актуальна версія Політики завжди доступна на цій сторінці.</p>
        </section>

        <p className="text-white/60 text-xs mt-6">Дата останнього оновлення: січень 2026 р.</p>
      </div>
    </LegalPage>
  );
}


function PaymentDemoCard() {
  const { lang } = useLangBase();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState('basic');
  const [customAmount, setCustomAmount] = useState('');

  const isUa = lang === 'ua';

  // Min/max amounts in UAH
  const MIN_AMOUNT_UAH = 10;
  const MAX_AMOUNT_UAH = 100000;

  const plans = useMemo(
    () => [
      {
        id: 'basic',
        name: isUa ? 'Basic' : 'Basic',
        priceUahPerMonth: 1999,
        description: isUa ? 'Доступ до базових AI-сервісів.' : 'Access to core AI services.'
      },
      {
        id: 'pro',
        name: isUa ? 'Pro' : 'Pro',
        priceUahPerMonth: 4999,
        description: isUa
          ? 'Розширена автоматизація, пріоритетна підтримка та індивідуальні інтеграції.'
          : 'Expanded automation, priority support, and custom integrations.'
      },
      {
        id: 'custom',
        name: isUa ? 'Довільна сума' : 'Custom Amount',
        priceUahPerMonth: null,
        description: isUa ? 'Введіть власну суму для оплати.' : 'Enter your own payment amount.'
      }
    ],
    [isUa]
  );

  const selectedPlan = plans.find((p) => p.id === selectedPlanId) || plans[0];
  
  // Calculate amount in kopecks
  const getAmountKopecks = () => {
    if (selectedPlanId === 'custom') {
      const parsed = parseFloat(customAmount);
      return isNaN(parsed) ? 0 : Math.round(parsed * 100);
    }
    return selectedPlan.priceUahPerMonth * 100;
  };
  
  const amountKopecks = getAmountKopecks();
  const displayAmount = selectedPlanId === 'custom' 
    ? (customAmount ? parseFloat(customAmount) : 0) 
    : selectedPlan.priceUahPerMonth;

  // Validate custom amount
  const validateAmount = () => {
    if (selectedPlanId !== 'custom') return null;
    const parsed = parseFloat(customAmount);
    if (!customAmount || isNaN(parsed)) {
      return isUa ? 'Введіть суму' : 'Enter an amount';
    }
    if (parsed < MIN_AMOUNT_UAH) {
      return isUa ? `Мінімальна сума: ₴${MIN_AMOUNT_UAH}` : `Minimum amount: ₴${MIN_AMOUNT_UAH}`;
    }
    if (parsed > MAX_AMOUNT_UAH) {
      return isUa ? `Максимальна сума: ₴${MAX_AMOUNT_UAH}` : `Maximum amount: ₴${MAX_AMOUNT_UAH}`;
    }
    return null;
  };

  const amountError = validateAmount();
  const isPayDisabled = loading || (selectedPlanId === 'custom' && amountError);

  return (
    <Card testId="payment-demo-card" className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-white" data-testid="payment-demo-title">
          {isUa ? 'Оплата' : 'Payment'}
        </div>
        <Pill testId="payment-demo-pill">UPC</Pill>
      </div>

      <div className="text-sm text-white/75" data-testid="payment-demo-description">
        {isUa
          ? 'Оберіть тариф і перейдіть на захищену платіжну сторінку UPC (hosted payment page).'
          : 'Select a plan and redirect to UPC secure hosted payment page.'}
      </div>

      <div className="grid gap-3" data-testid="plan-selector">
        {plans.map((p) => {
          const active = p.id === selectedPlanId;
          const isCustom = p.id === 'custom';
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setSelectedPlanId(p.id)}
              className={`rounded-2xl border bg-white/[0.02] p-4 text-left transition-colors ${
                active ? 'border-white/25 bg-white/[0.04]' : 'border-white/10 hover:bg-white/[0.04]'
              }`}
              data-testid={`plan-option-${p.id}`}
              aria-pressed={active}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-white" data-testid={`plan-name-${p.id}`}>{p.name}</div>
                  <div className="mt-1 text-xs text-white/65" data-testid={`plan-desc-${p.id}`}>{p.description}</div>
                </div>
                <div className="text-right">
                  {isCustom ? (
                    <div className="text-sm font-semibold text-white/50" data-testid={`plan-price-${p.id}`}>
                      {isUa ? 'Ваша сума' : 'Your amount'}
                    </div>
                  ) : (
                    <>
                      <div className="text-sm font-semibold text-white" data-testid={`plan-price-${p.id}`}>₴{p.priceUahPerMonth}/mo</div>
                      <div className="mt-1 text-[11px] text-white/55" data-testid={`plan-price-format-${p.id}`}>UAH monthly</div>
                    </>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Custom amount input */}
      {selectedPlanId === 'custom' && (
        <div className="space-y-2" data-testid="custom-amount-section">
          <label className="block text-xs font-medium text-white/75" htmlFor="custom-amount-input">
            {isUa ? 'Сума оплати (UAH)' : 'Payment amount (UAH)'}
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50">₴</span>
            <input
              id="custom-amount-input"
              type="number"
              min={MIN_AMOUNT_UAH}
              max={MAX_AMOUNT_UAH}
              step="0.01"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              placeholder={isUa ? 'Введіть суму' : 'Enter amount'}
              className="w-full rounded-xl border border-white/15 bg-white/[0.03] py-3 pl-8 pr-4 text-sm text-white placeholder-white/40 outline-none transition-colors focus:border-white/30 focus:bg-white/[0.05]"
              data-testid="custom-amount-input"
            />
          </div>
          {amountError && (
            <div className="text-xs text-amber-400" data-testid="custom-amount-error">
              {amountError}
            </div>
          )}
          <div className="text-[11px] text-white/50">
            {isUa 
              ? `Мін: ₴${MIN_AMOUNT_UAH} · Макс: ₴${MAX_AMOUNT_UAH.toLocaleString()}`
              : `Min: ₴${MIN_AMOUNT_UAH} · Max: ₴${MAX_AMOUNT_UAH.toLocaleString()}`}
          </div>
        </div>
      )}

      {error ? (
        <div className="rounded-2xl border border-red-400/20 bg-red-400/10 px-4 py-3 text-xs text-red-100" data-testid="payment-demo-error">
          {error}
        </div>
      ) : null}

      <button
        type="button"
        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-500 via-violet-500 to-cyan-400 px-5 py-3 text-sm font-semibold text-ink-950 shadow-glow transition-transform duration-200 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-70"
        disabled={isPayDisabled}
        onClick={async () => {
          setError('');
          
          // Final validation before payment
          if (selectedPlanId === 'custom') {
            const validationError = validateAmount();
            if (validationError) {
              setError(validationError);
              return;
            }
          }
          
          setLoading(true);
          try {
            // UPC OrderID max 20 chars. Use short format: NF + plan code + timestamp suffix
            const planCode = selectedPlanId === 'basic' ? 'B' : selectedPlanId === 'pro' ? 'P' : 'C';
            const orderId = `NF${planCode}${Date.now().toString().slice(-10)}`;
            
            const description = selectedPlanId === 'custom'
              ? (isUa ? `NeuralForge оплата ₴${displayAmount}` : `NeuralForge payment ₴${displayAmount}`)
              : `NeuralForge ${selectedPlan.name} Plan (monthly)`;
            
            const res = await apiPostJson('/api/payments/create', {
              orderId,
              amount: amountKopecks,
              currency: '980',
              description,
              returnUrl: 'https://www.neuralforge.ink/payment/return',
              locale: isUa ? 'uk' : 'en'
            });

            postRedirect({
              url: res.gatewayUrl,
              fields: res.fields,
              testId: 'payment-redirect-form'
            });
          } catch (e) {
            setError(e?.message || 'Payment initiation failed');
            setLoading(false);
          }
        }}
        data-testid="pay-button"
      >
        {loading 
          ? (isUa ? 'Підготовка…' : 'Preparing…') 
          : (isUa ? `Сплатити ₴${displayAmount || 0}` : `Pay ₴${displayAmount || 0}`)}
      </button>

      <div className="text-[11px] text-white/55" data-testid="payment-demo-note">
        {isUa
          ? 'Оплата обробляється на стороні UPC. Сертифікати та підпис зберігаються лише на сервері.'
          : 'Payments are processed by UPC. Certificates and signing stay server-side only.'}
      </div>
    </Card>
  );
}


function SubscriptionPage() {
  useRevealOnScroll();
  const { lang, base } = useLangBase();
 

  return (
    <div className="pt-28" data-testid="subscription-page">
      <Container>
        <SectionHeader
          testId="subscription-header"
          eyebrow={lang === 'ua' ? 'Послуги та підписка' : 'Services & Subscription'}
          title={lang === 'ua' ? 'Підписка на AI-інструменти та бізнес-послуги' : 'Subscription-based access to AI tools and business services'}
          description={
            lang === 'ua'
              ? 'Ми надаємо послуги у цифровому форматі. Спочатку — звернення через сайт, далі — консультація та доступ після узгодження.'
              : 'We deliver AI services digitally. Customers contact us through the website, then receive access after an initial consultation.'
          }
        />

        <div className="mt-10 grid gap-6 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Card testId="subscription-description" className="space-y-4">
              <div className="text-sm font-semibold text-white" data-testid="subscription-desc-title">What you get</div>
              <div className="grid gap-2 text-sm text-white/75" data-testid="subscription-desc-list">
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">AI consulting and implementation</div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">Business process automation</div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">Generative AI tools</div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">Data analytics and forecasting</div>
              </div>

              <div className="mt-2 rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid="subscription-delivery">
                Services are delivered digitally. Access is provided after an initial consultation.
              </div>
            </Card>
          </div>

          <div className="lg:col-span-5">
            <Card testId="subscription-pricing" className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-white" data-testid="subscription-plan-title">Basic Plan</div>
                <Pill testId="subscription-plan-pill">One plan</Pill>
              </div>
              <div className="text-sm text-white/75" data-testid="subscription-plan-description">
                Basic Plan — subscription-based access to core AI services.
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 text-sm text-white/75" data-testid="subscription-pricing-text">
                Pricing details are provided upon request after initial consultation.
              </div>
              <PrimaryButton to={withBase(base, '/contact')} testId="subscription-cta">{lang === 'ua' ? 'Звʼязатися' : 'Contact Us'}</PrimaryButton>
              <div className="pt-2" data-testid="subscription-payment-demo">
                <PaymentDemoCard />
              </div>
            </Card>
          </div>
        </div>
      </Container>
    </div>
  );
}


function PaymentReturnPage() {
  const { lang } = useLangBase();
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const orderId = query.get('orderId') || query.get('ORDER') || '';
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!orderId) return;
      try {
        const res = await apiGetJson(`/api/payments/${encodeURIComponent(orderId)}`);
        if (!cancelled) setStatus(res);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load payment status');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  return (
    <div className="pt-28" data-testid="payment-return-page">
      <Container>
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-10 shadow-glow">
          <div className="text-xs font-semibold uppercase tracking-wider text-white/55" data-testid="payment-return-eyebrow">
            {lang === 'ua' ? 'Оплата' : 'Payment'}
          </div>
          <div className="mt-3 text-3xl font-semibold tracking-tight text-white" data-testid="payment-return-title">
            {lang === 'ua' ? 'Повернення з платіжної сторінки' : 'Returned from payment page'}
          </div>

          <div className="mt-4 text-sm text-white/75" data-testid="payment-return-description">
            {lang === 'ua'
              ? 'Якщо платіж пройшов успішно, статус оновиться після підтвердження від шлюзу.'
              : 'If payment was successful, status will update after gateway confirmation.'}
          </div>

          {orderId ? (
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-3 text-sm text-white/75" data-testid="payment-return-order">
              <span className="text-white/55">Order:</span> {orderId}
            </div>
          ) : (
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-3 text-sm text-white/75" data-testid="payment-return-no-order">
              {lang === 'ua' ? 'OrderId не передано в returnUrl.' : 'No orderId was provided in returnUrl.'}
            </div>
          )}

          {error ? (
            <div className="mt-4 rounded-2xl border border-red-400/20 bg-red-400/10 px-4 py-3 text-sm text-red-100" data-testid="payment-return-error">
              {error}
            </div>
          ) : null}

          {status ? (
            <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-3 text-sm text-white/75" data-testid="payment-return-status">
              <span className="text-white/55">Status:</span> {status.status}
            </div>
          ) : null}

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <PrimaryButton to="/subscription" testId="payment-return-back">{lang === 'ua' ? 'Назад' : 'Back'}</PrimaryButton>
            <SecondaryButton to="/contact" testId="payment-return-contact">{lang === 'ua' ? 'Підтримка' : 'Contact support'}</SecondaryButton>
          </div>
        </div>
      </Container>
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className="pt-28" data-testid="not-found-page">
      <Container>
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-10 shadow-glow">
          <div className="text-2xl font-semibold text-white" data-testid="not-found-title">Page not found</div>
          <div className="mt-2 text-sm text-white/70" data-testid="not-found-subtitle">
            The page you’re looking for doesn’t exist.
          </div>
          <div className="mt-6 flex gap-3">
            <PrimaryButton to="/" testId="not-found-home">Go Home</PrimaryButton>
            <SecondaryButton to="/contact" testId="not-found-contact">Contact Us</SecondaryButton>
          </div>
        </div>
      </Container>
    </div>
  );
}

function AppShell() {
  useRevealOnScroll();
  return (
    <div className="min-h-screen">
      <FloatingNav />
      <Routes>
        {/* EN */}
        <Route path="/" element={<HomePage lang="en" base="" />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/case-studies" element={<CaseStudiesPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/resources" element={<ResourcesPage />} />
        <Route path="/subscription" element={<SubscriptionPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/payment/return" element={<PaymentReturnPage />} />

        {/* UA */}
        <Route path="/ua" element={<HomePage lang="ua" base="/ua" />} />
        <Route path="/ua/services" element={<ServicesPage />} />
        <Route path="/ua/how-it-works" element={<HowItWorksPage />} />
        <Route path="/ua/case-studies" element={<CaseStudiesPage />} />
        <Route path="/ua/about" element={<AboutPage />} />
        <Route path="/ua/resources" element={<ResourcesPage />} />
        <Route path="/ua/subscription" element={<SubscriptionPage />} />
        <Route path="/ua/contact" element={<ContactPage />} />
        <Route path="/ua/payment/return" element={<PaymentReturnPage />} />
        <Route path="/ua/offer-agreement" element={<OfferAgreementPageUA />} />
        <Route path="/ua/personal-data-policy" element={<PersonalDataPolicyPageUA />} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter future={routerFuture}>
      <ScrollToTop />
      <AppShell />
    </BrowserRouter>
  );
}
